import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Reply, Topic, ReplyComment } from '../types';
import { ArrowLeft, MessageSquare, Eye, ThumbsUp, CornerDownRight, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { getMemberDisplayName, getCachedDisplayName } from '@/utils/memberUtils';

export default function TopicDetail() {
  const { topicId } = useParams<{ topicId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [topic, setTopic] = useState<Topic | null>(null);
  const [replies, setReplies] = useState<Reply[]>([]);
  const [newReply, setNewReply] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [likedReplies, setLikedReplies] = useState<Record<string, boolean>>({});
  const [memberData, setMemberData] = useState<any>(null);
  
  // New state for comments on replies
  const [comments, setComments] = useState<ReplyComment[]>([]);
  const [replyIdToComment, setReplyIdToComment] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [likedComments, setLikedComments] = useState<Record<string, boolean>>({});

  // Add real-time subscription for replies
  useEffect(() => {
    if (!topicId) return;
    
    const channel = supabase
      .channel('hub_replies_changes')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'hub_replies',
        filter: `topic_id=eq.${topicId}`
      }, () => {
        console.log('New reply received');
        fetchReplies();
      })
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [topicId]);

  // Add real-time subscription for reply likes
  useEffect(() => {
    if (!topicId || !user) return;
    
    const channel = supabase
      .channel('hub_reply_likes_changes')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'hub_reply_likes',
        filter: `topic_id=eq.${topicId}`
      }, () => {
        console.log('New like received');
        fetchLikedReplies();
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'hub_reply_likes',
        filter: `topic_id=eq.${topicId}`
      }, () => {
        console.log('Like removed');
        fetchLikedReplies();
      })
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [topicId, user]);

  // Add real-time subscription for reply comments
  useEffect(() => {
    if (!topicId) return;
    
    const channel = supabase
      .channel('hub_reply_comments_changes')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'hub_reply_comments',
        filter: `topic_id=eq.${topicId}`
      }, () => {
        console.log('New comment received');
        fetchComments();
      })
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [topicId]);

  // Add real-time subscription for comment likes
  useEffect(() => {
    if (!topicId || !user) return;
    
    const channel = supabase
      .channel('hub_reply_comment_likes_changes')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'hub_reply_comment_likes',
        filter: `topic_id=eq.${topicId}`
      }, () => {
        console.log('New comment like received');
        fetchLikedComments();
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'hub_reply_comment_likes',
        filter: `topic_id=eq.${topicId}`
      }, () => {
        console.log('Comment like removed');
        fetchLikedComments();
      })
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [topicId, user]);

  // Fetch member data when user is available
  useEffect(() => {
    if (user) {
      fetchMemberData();
    }
  }, [user]);

  const fetchMemberData = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('community_members')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      setMemberData(data);
    } catch (err) {
      console.error('Error fetching member data:', err);
    }
  };

  useEffect(() => {
    if (!topicId) return;
    
    const fetchTopicData = async () => {
      setLoading(true);
      try {
        console.log('Fetching topic data for ID:', topicId);
        
        // Fetch the topic
        const { data: topicData, error: topicError } = await supabase
          .from('hub_topics')
          .select('*')
          .eq('id', topicId)
          .single();

        if (topicError) {
          console.error('Error fetching topic:', topicError);
          throw topicError;
        }
        
        console.log('Topic data fetched:', topicData);
        
        // Ensure author profile exists
        await supabase.rpc('ensure_profile_exists', { user_id: topicData.author_id });
        
        // Fetch author from community_members instead of profiles
        const { data: authorMember, error: authorError } = await supabase
          .from('community_members')
          .select('nickname, full_name, avatar_url')
          .eq('id', topicData.author_id)
          .single();
          
        console.log('Author member:', authorMember, 'Error:', authorError);
        
        const authorName = authorError ? `User ${topicData.author_id.substring(0, 6)}` : 
          (authorMember?.nickname || authorMember?.full_name || `User ${topicData.author_id.substring(0, 6)}`);
        const authorAvatar = authorError ? null : authorMember?.avatar_url;
        
        // Increment view count
        const { error: updateError } = await supabase
          .from('hub_topics')
          .update({ view_count: (topicData.view_count || 0) + 1 })
          .eq('id', topicId);
          
        if (updateError) console.error('Error updating view count:', updateError);
        
        // Format the topic data
        const formattedTopic: Topic = {
          id: topicData.id,
          title: topicData.title,
          content: topicData.content,
          author: {
            id: topicData.author_id,
            name: authorName,
            avatar: authorAvatar
          },
          category: topicData.category || 'General',
          created_at: topicData.created_at,
          reply_count: 0, // Will be updated after fetching replies
          view_count: (topicData.view_count || 0) + 1, // Increment locally for immediate UI update
          last_activity: topicData.last_activity || topicData.created_at
        };
        
        setTopic(formattedTopic);
        
        // Fetch the replies
        fetchReplies();
        
        // Fetch comments
        fetchComments();
        
        // If user is logged in, fetch which replies they've liked
        if (user) {
          fetchLikedReplies();
          fetchLikedComments();
        }
      } catch (err) {
        console.error('Error fetching topic:', err);
        toast.error('Failed to load topic. It may have been deleted or does not exist.');
        navigate('/community');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTopicData();
  }, [topicId, navigate, user]);
  
  const fetchReplies = async () => {
    if (!topicId) return;
    
    try {
      console.log('Fetching replies for topic:', topicId);
      
      const { data, error } = await supabase
        .from('hub_replies')
        .select('*')
        .eq('topic_id', topicId)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      
      console.log('Replies data fetched:', data);
      
      // Fetch all author members in one batch
      if (data && data.length > 0) {
        const authorIds = [...new Set(data.map(reply => reply.author_id))];
        
        // Ensure all author profiles exist
        for (const authorId of authorIds) {
          await supabase.rpc('ensure_profile_exists', { user_id: authorId });
        }
        
        const { data: authorMembers, error: membersError } = await supabase
          .from('community_members')
          .select('id, nickname, full_name, avatar_url')
          .in('id', authorIds);
        
        if (membersError) {
          console.error('Error fetching author members:', membersError);
        }
        
        // Create a map of author members for quick lookup
        const membersMap = (authorMembers || []).reduce((map: Record<string, any>, member) => {
          map[member.id] = member;
          return map;
        }, {});
        
        // Format the replies data with author information
        const formattedReplies: Reply[] = (data || []).map((item: any) => {
          const member = membersMap[item.author_id];
          const displayName = member ? getCachedDisplayName(membersMap, item.author_id) : `User ${item.author_id.substring(0, 6)}`;
          
          return {
            id: item.id,
            content: item.content,
            author: {
              id: item.author_id,
              name: displayName,
              avatar: member?.avatar_url || null
            },
            created_at: item.created_at,
            likes: item.likes || 0,
            isLiked: likedReplies[item.id] || false
          };
        });
        
        setReplies(formattedReplies);
        
        if (topic) {
          setTopic({
            ...topic,
            reply_count: formattedReplies.length
          });
        }
      } else {
        setReplies([]);
      }
      
    } catch (err) {
      console.error('Error fetching replies:', err);
      toast.error('Failed to load replies');
    }
  };

  // Fetch comments for all replies
  const fetchComments = async () => {
    if (!topicId) return;
    
    try {
      console.log('Fetching comments for topic:', topicId);
      
      const { data, error } = await supabase
        .from('hub_reply_comments')
        .select('*')
        .eq('topic_id', topicId)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      
      console.log('Comments data fetched:', data);
      
      if (data && data.length > 0) {
        // Fetch all author members in one batch
        const authorIds = [...new Set(data.map(comment => comment.author_id))];
        
        // Ensure all profiles exist
        for (const authorId of authorIds) {
          await supabase.rpc('ensure_profile_exists', { user_id: authorId });
        }
        
        const { data: authorMembers, error: membersError } = await supabase
          .from('community_members')
          .select('id, nickname, full_name, avatar_url')
          .in('id', authorIds);
        
        if (membersError) {
          console.error('Error fetching comment author members:', membersError);
        }
        
        // Create a map of author members
        const membersMap = (authorMembers || []).reduce((map: Record<string, any>, member) => {
          map[member.id] = member;
          return map;
        }, {});
        
        // Format the comments data
        const formattedComments: ReplyComment[] = (data || []).map((item: any) => {
          const member = membersMap[item.author_id];
          const displayName = member ? getCachedDisplayName(membersMap, item.author_id) : `User ${item.author_id.substring(0, 6)}`;
          
          return {
            id: item.id,
            content: item.content,
            author: {
              id: item.author_id,
              name: displayName,
              avatar: member?.avatar_url || null
            },
            created_at: item.created_at,
            likes: item.likes || 0,
            isLiked: likedComments[item.id] || false,
            reply_id: item.reply_id
          };
        });
        
        setComments(formattedComments);
      } else {
        setComments([]);
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  };

  const fetchLikedReplies = async () => {
    if (!user || !topicId) return;
    
    try {
      // Using a type assertion to inform TypeScript about our custom function
      const { data, error } = await supabase.rpc(
        'get_user_liked_replies' as any, 
        { 
          user_id_param: user.id,
          topic_id_param: topicId
        }
      );
        
      if (error) throw error;
      
      const likedMap: Record<string, boolean> = {};
      
      if (data && Array.isArray(data)) {
        data.forEach((item) => {
          // If found in the results, user has liked this reply
          likedMap[item.reply_id] = true;
        });
      }
      
      setLikedReplies(likedMap);
      
      // Update the isLiked flag for each reply
      setReplies((current) =>
        current.map((reply) => ({
          ...reply,
          isLiked: likedMap[reply.id] || false,
        }))
      );
    } catch (err) {
      console.error('Error fetching liked replies:', err);
    }
  };

  const fetchLikedComments = async () => {
    if (!user || !topicId) return;
    
    try {
      // Using a type assertion for our custom function
      const { data, error } = await supabase.rpc(
        'get_user_liked_comments' as any, 
        { 
          user_id_param: user.id,
          topic_id_param: topicId
        }
      );
        
      if (error) throw error;
      
      const likedMap: Record<string, boolean> = {};
      
      if (data && Array.isArray(data)) {
        data.forEach((item) => {
          // If found in the results, user has liked this comment
          likedMap[item.comment_id] = true;
        });
      }
      
      setLikedComments(likedMap);
      
      // Update the isLiked flag for each comment
      setComments((current) =>
        current.map((comment) => ({
          ...comment,
          isLiked: likedMap[comment.id] || false,
        }))
      );
    } catch (err) {
      console.error('Error fetching liked comments:', err);
    }
  };
  
  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !topicId || !newReply.trim()) return;
    
    setIsSubmitting(true);
    try {
      console.log('Posting reply for topic:', topicId);
      
      // Ensure the user has a profile using our new database function
      await supabase.rpc('ensure_profile_exists', { user_id: user.id });

      // Get member data if not already loaded
      if (!memberData) {
        await fetchMemberData();
      }
      
      // Create the reply
      const { data: replyData, error: replyError } = await supabase
        .from('hub_replies')
        .insert({
          content: newReply.trim(),
          topic_id: topicId,
          author_id: user.id,
        })
        .select()
        .single();
        
      if (replyError) {
        console.error('Error posting reply:', replyError);
        throw replyError;
      }
      
      console.log('Reply posted successfully:', replyData);
      
      // Update last activity on the topic
      const { error: updateError } = await supabase
        .from('hub_topics')
        .update({ last_activity: new Date().toISOString() })
        .eq('id', topicId);
        
      if (updateError) console.error('Error updating last activity:', updateError);
      
      // Clear the reply input
      setNewReply('');
      toast.success('Reply posted successfully');
      
      // fetchReplies is no longer needed here since we have a real-time subscription
    } catch (err) {
      console.error('Error posting reply:', err);
      toast.error('Failed to post reply. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !topicId || !replyIdToComment || !newComment.trim()) return;
    
    setSubmittingComment(true);
    try {
      console.log('Posting comment for reply:', replyIdToComment);
      
      // Ensure the user has a profile
      await supabase.rpc('ensure_profile_exists', { user_id: user.id });

      // Get member data if not already loaded
      if (!memberData) {
        await fetchMemberData();
      }
      
      // Create the comment
      const { data: commentData, error: commentError } = await supabase
        .from('hub_reply_comments')
        .insert({
          content: newComment.trim(),
          topic_id: topicId,
          reply_id: replyIdToComment,
          author_id: user.id,
        })
        .select()
        .single();
        
      if (commentError) {
        console.error('Error posting comment:', commentError);
        throw commentError;
      }
      
      console.log('Comment posted successfully:', commentData);
      
      // Update last activity on the topic
      const { error: updateError } = await supabase
        .from('hub_topics')
        .update({ last_activity: new Date().toISOString() })
        .eq('id', topicId);
        
      if (updateError) console.error('Error updating last activity:', updateError);
      
      // Clear the comment input and close the comment form
      setNewComment('');
      setReplyIdToComment(null);
      toast.success('Comment posted successfully');
      
      // fetchComments is no longer needed here due to real-time subscription
    } catch (err) {
      console.error('Error posting comment:', err);
      toast.error('Failed to post comment. Please try again.');
    } finally {
      setSubmittingComment(false);
    }
  };
  
  const handleLikeReply = async (replyId: string, isLiked: boolean) => {
    if (!user || !topicId) {
      toast.error('You need to be logged in to like replies');
      return;
    }
    
    // Optimistically update the UI
    setReplies((currentReplies) =>
      currentReplies.map((reply) => {
        if (reply.id === replyId) {
          return {
            ...reply,
            likes: isLiked ? Math.max(0, reply.likes - 1) : reply.likes + 1,
            isLiked: !isLiked,
          };
        }
        return reply;
      })
    );
    
    // Also update the liked status in our local state
    setLikedReplies((current) => ({
      ...current,
      [replyId]: !isLiked,
    }));
    
    try {
      if (isLiked) {
        // Use type assertion for our custom function
        await supabase.rpc(
          'custom_unlike_reply_wrapper' as any, 
          {
            user_id_param: user.id,
            reply_id_param: replyId,
            topic_id_param: topicId
          }
        );
      } else {
        // Use type assertion for our custom function
        await supabase.rpc(
          'custom_like_reply_wrapper' as any, 
          {
            user_id_param: user.id,
            reply_id_param: replyId,
            topic_id_param: topicId
          }
        );
      }
    } catch (err) {
      console.error('Error updating like:', err);
      toast.error('Failed to update like');
      
      // Revert the optimistic UI update on error
      fetchReplies();
      fetchLikedReplies();
    }
  };

  const handleLikeComment = async (commentId: string, replyId: string, isLiked: boolean) => {
    if (!user || !topicId) {
      toast.error('You need to be logged in to like comments');
      return;
    }
    
    // Optimistically update the UI
    setComments((currentComments) =>
      currentComments.map((comment) => {
        if (comment.id === commentId) {
          return {
            ...comment,
            likes: isLiked ? Math.max(0, comment.likes - 1) : comment.likes + 1,
            isLiked: !isLiked,
          };
        }
        return comment;
      })
    );
    
    // Also update the liked status in our local state
    setLikedComments((current) => ({
      ...current,
      [commentId]: !isLiked,
    }));
    
    try {
      if (isLiked) {
        // Use type assertion for our custom function
        await supabase.rpc(
          'unlike_reply_comment_wrapper' as any, 
          {
            user_id_param: user.id,
            comment_id_param: commentId
          }
        );
      } else {
        // Use type assertion for our custom function
        await supabase.rpc(
          'like_reply_comment_wrapper' as any, 
          {
            user_id_param: user.id,
            comment_id_param: commentId,
            reply_id_param: replyId,
            topic_id_param: topicId
          }
        );
      }
    } catch (err) {
      console.error('Error updating comment like:', err);
      toast.error('Failed to update like');
      
      // Revert the optimistic UI update on error
      fetchComments();
      fetchLikedComments();
    }
  };
  
  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">
        <div className="w-8 h-8 border-4 border-t-omoduduwa-terracotta border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin mx-auto mb-4"></div>
        Loading topic...
      </div>
    );
  }
  
  if (!topic) {
    return (
      <div className="p-8 text-center text-gray-500">
        Topic not found or has been deleted.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button 
        variant="ghost" 
        className="flex items-center gap-2 mb-4 hover:bg-omoduduwa-terracotta/10"
        onClick={() => navigate('/community')}
      >
        <ArrowLeft size={16} /> Back to Hub
      </Button>
      
      <Card className="border-omoduduwa-terracotta/20 shadow-md overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-omoduduwa-terracotta/10 to-transparent border-b">
          <div className="space-y-2">
            <CardTitle className="text-2xl font-semibold text-omoduduwa-indigo">{topic.title}</CardTitle>
            <div className="flex flex-wrap gap-6 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <MessageSquare size={14} /> {replies.length} replies
              </span>
              <span className="flex items-center gap-1">
                <Eye size={14} /> {topic.view_count} views
              </span>
              <span className="px-2 py-1 bg-omoduduwa-terracotta/10 text-omoduduwa-terracotta rounded-full text-xs">
                {topic.category}
              </span>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-6">
          <div className="flex gap-4">
            <div>
              <Avatar className="h-10 w-10 border-2 border-omoduduwa-terracotta/20">
                <AvatarImage src={topic.author.avatar || undefined} />
                <AvatarFallback className="bg-omoduduwa-indigo text-white">{topic.author.name.charAt(0)}</AvatarFallback>
              </Avatar>
            </div>
            <div className="flex-1 space-y-2">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-omoduduwa-indigo">{topic.author.name}</p>
                  <p className="text-sm text-gray-500">
                    Posted {formatDistanceToNow(new Date(topic.created_at), { addSuffix: true })}
                  </p>
                </div>
              </div>
              <div className="prose max-w-none text-omoduduwa-text">
                <p style={{ whiteSpace: 'pre-wrap' }}>{topic.content}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <div className="space-y-6">
        <h3 className="text-xl font-semibold text-omoduduwa-indigo flex items-center gap-2">
          <MessageSquare size={18} className="text-omoduduwa-terracotta" /> Replies ({replies.length})
        </h3>
        
        {user ? (
          <form onSubmit={handleSubmitReply} className="space-y-4">
            <Textarea
              value={newReply}
              onChange={(e) => setNewReply(e.target.value)}
              placeholder="Write a reply..."
              rows={4}
              className="border-omoduduwa-terracotta/20 focus:border-omoduduwa-terracotta focus:ring-omoduduwa-terracotta/20"
            />
            <div className="flex justify-end">
              <Button 
                type="submit" 
                disabled={isSubmitting || !newReply.trim()}
                className="bg-omoduduwa-terracotta hover:bg-omoduduwa-terracotta/90 text-white"
              >
                {isSubmitting ? 'Posting...' : 'Post Reply'}
              </Button>
            </div>
          </form>
        ) : (
          <div className="text-center p-4 bg-omoduduwa-offwhite rounded-md border border-omoduduwa-terracotta/20">
            You need to be logged in to reply to this topic.
          </div>
        )}
        
        {replies.length === 0 ? (
          <div className="text-center text-gray-500 py-8 bg-omoduduwa-offwhite rounded-md">
            <MessageSquare className="mx-auto h-8 w-8 text-gray-300 mb-2" />
            No replies yet. Be the first to reply!
          </div>
        ) : (
          <div className="space-y-4">
            {replies.map((reply, index) => {
              // Filter comments for this reply
              const replyComments = comments.filter(comment => comment.reply_id === reply.id);
              
              return (
                <motion.div
                  key={reply.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                >
                  <Card key={reply.id} className="border-omoduduwa-terracotta/10 hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex gap-4">
                        <div>
                          <Avatar className="h-10 w-10 border border-omoduduwa-terracotta/20">
                            <AvatarImage src={reply.author.avatar || undefined} />
                            <AvatarFallback className="bg-omoduduwa-indigo/80 text-white">{reply.author.name.charAt(0)}</AvatarFallback>
                          </Avatar>
                        </div>
                        <div className="flex-1 space-y-2">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-omoduduwa-indigo">{reply.author.name}</p>
                              <p className="text-sm text-gray-500">
                                {formatDistanceToNow(new Date(reply.created_at), { addSuffix: true })}
                              </p>
                            </div>
                          </div>
                          <div className="text-omoduduwa-text">
                            <p style={{ whiteSpace: 'pre-wrap' }}>{reply.content}</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter className="px-6 py-3 bg-omoduduwa-offwhite border-t flex justify-between">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="flex items-center gap-1 text-omoduduwa-indigo hover:bg-omoduduwa-indigo/10"
                        onClick={() => user ? setReplyIdToComment(replyIdToComment === reply.id ? null : reply.id) : toast.error('You need to be logged in to comment')}
                        disabled={!user}
                      >
                        <MessageSquare size={14} /> {replyComments.length > 0 ? `${replyComments.length} comments` : 'Comment'}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className={`flex items-center gap-1 ${
                          reply.isLiked 
                            ? 'text-omoduduwa-terracotta hover:bg-omoduduwa-terracotta/10' 
                            : 'text-gray-600 hover:text-omoduduwa-terracotta'
                        }`}
                        onClick={() => handleLikeReply(reply.id, reply.isLiked)}
                        disabled={!user}
                      >
                        <ThumbsUp size={14} className={reply.isLiked ? 'fill-omoduduwa-terracotta' : ''} /> {reply.likes}
                      </Button>
                    </CardFooter>
                    
                    {/* Comments section for this reply */}
                    {replyComments.length > 0 && (
                      <div className="bg-omoduduwa-offwhite/50 px-6 py-3 border-t">
                        <h4 className="text-sm font-medium text-omoduduwa-indigo mb-3">Comments ({replyComments.length})</h4>
                        <div className="space-y-3 pl-6 border-l-2 border-omoduduwa-terracotta/20">
                          {replyComments.map((comment) => (
                            <div key={comment.id} className="bg-white p-3 rounded-md border border-omoduduwa-terracotta/10 shadow-sm">
                              <div className="flex gap-2">
                                <Avatar className="h-6 w-6">
                                  <AvatarImage src={comment.author.avatar || undefined} />
                                  <AvatarFallback className="bg-omoduduwa-indigo/70 text-white text-xs">{comment.author.name.charAt(0)}</AvatarFallback>
                                </Avatar>
                                <div className="flex-1">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <p className="text-sm font-medium">{comment.author.name}</p>
                                      <p className="text-xs text-gray-500">
                                        {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                                      </p>
                                    </div>
                                  </div>
                                  <p className="text-sm mt-1">{comment.content}</p>
                                  <div className="mt-1 flex justify-end">
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className={`flex items-center gap-1 text-xs py-0 h-auto px-2 ${
                                        comment.isLiked 
                                          ? 'text-omoduduwa-terracotta hover:bg-omoduduwa-terracotta/10' 
                                          : 'text-gray-500 hover:text-omoduduwa-terracotta'
                                      }`}
                                      onClick={() => handleLikeComment(comment.id, reply.id, comment.isLiked)}
                                      disabled={!user}
                                    >
                                      <ThumbsUp size={12} className={comment.isLiked ? 'fill-omoduduwa-terracotta' : ''} /> {comment.likes}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Comment form */}
                    {replyIdToComment === reply.id && (
                      <div className="bg-omoduduwa-offwhite/50 px-6 py-4 border-t">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-omoduduwa-indigo flex items-center gap-1">
                            <CornerDownRight size={14} className="text-omoduduwa-terracotta" /> 
                            Add a comment
                          </h4>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0 rounded-full"
                            onClick={() => setReplyIdToComment(null)}
                          >
                            <X size={14} />
                            <span className="sr-only">Close</span>
                          </Button>
                        </div>
                        <form onSubmit={handleSubmitComment} className="space-y-2">
                          <Textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Add your comment..."
                            rows={2}
                            className="border-omoduduwa-terracotta/20 focus:border-omoduduwa-terracotta focus:ring-omoduduwa-terracotta/20 text-sm resize-none"
                          />
                          <div className="flex justify-end">
                            <Button 
                              type="submit" 
                              size="sm"
                              disabled={submittingComment || !newComment.trim()}
                              className="bg-omoduduwa-terracotta hover:bg-omoduduwa-terracotta/90 text-white text-xs"
                            >
                              {submittingComment ? 'Posting...' : 'Post Comment'}
                            </Button>
                          </div>
                        </form>
                      </div>
                    )}
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
